import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Handles the Emergent-managed Google Auth callback.
 * The user lands here as `/auth/callback#session_id=<...>` after Google auth.
 * We synchronously parse the fragment, POST to /api/auth/emergent-session,
 * store the returned JWT, and hard-redirect to /app.
 *
 * useRef guard prevents double-processing under React StrictMode (which double-invokes effects).
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      toast.error("Google sign-in didn't return a session. Please try again.");
      nav("/login", { replace: true });
      return;
    }

    const invite = sessionStorage.getItem("viyora_invite_token") || undefined;

    (async () => {
      try {
        const { data } = await api.post("/auth/emergent-session", {
          session_id: sessionId,
          invite_token: invite,
        });
        // Auth token now lives in an httpOnly cookie set by the backend.
        // Only the user-profile mirror stays in localStorage for fast paint.
        localStorage.setItem("dentalai_user", JSON.stringify(data.user));
        sessionStorage.removeItem("viyora_invite_token");
        // Clean the URL fragment before landing on /app
        window.history.replaceState({}, "", "/auth/callback");
        toast.success(data.new_user ? "Welcome to Viyora!" : "Welcome back");
        window.location.href = "/app";
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
  }, [nav]);

  return (
    <div className="min-h-screen bg-linen grid place-items-center p-8" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="w-8 h-8 mx-auto text-ink animate-spin" />
        <h1 className="mt-6 font-display text-2xl">Signing you in…</h1>
        <p className="mt-2 text-sm text-ink-mid">Setting up your Viyora session.</p>
      </div>
    </div>
  );
}
